import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import '@fontsource-variable/inter/index.css'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import '@vaisala/fonts/vaisala-sans-condensed-italic.css'
import '@vaisala/fonts/vaisala-sans-condensed.css'
import '@vaisala/fonts/vaisala-sans-italic.css'
import '@vaisala/fonts/vaisala-sans-mono.css'
import '@vaisala/fonts/vaisala-sans-vf-italic.css'
import '@vaisala/fonts/vaisala-sans-vf.css'
import '@vaisala/fonts/vaisala-sans.css'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { authManager } from './auth-manager.ts'
import { SplashScreen } from './components/SplashScreen.tsx'
import { env } from './env.ts'
import './i18n/i18n.ts'
import { queryClient } from './query-client.ts'
import { router } from './router.tsx'
import './sentry.ts'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { FlagOverridesMenu, LDProvider } from './launchdarkly.ts'
import { muiTheme } from './theme/theme.ts'

console.info(__APP_VERSION__)

const root = document.getElementById('root')
if (!root) throw new Error('Failed to find the root element')

const App = () => {
	const ldClient = useLDClient()
	const auth = useAuth0()
	authManager.setAuthContext(auth)

	if (auth.isLoading) {
		return <SplashScreen />
	}
	return <RouterProvider router={router} context={{ ldClient }} />
}

createRoot(root, {
	onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
		console.warn('Uncaught error', error, errorInfo.componentStack)
	}),
	onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
		if (process.env.NODE_ENV === 'development') {
			console.warn('Caught error', error, errorInfo.componentStack)
		}
	}),
	onRecoverableError: Sentry.reactErrorHandler(),
}).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			<ThemeProvider theme={muiTheme} storageManager={null}>
				<CssBaseline />
				<LDProvider>
					<Auth0Provider
						useRefreshTokens
						domain={env.AUTH_DOMAIN}
						clientId={env.AUTH_CLIENT_ID}
						authorizationParams={{
							redirect_uri: location.origin,
							audience: env.AUTH_AUDIENCE,
							scope: 'openid profile email offline_access',
						}}
						cacheLocation='localstorage'
					>
						<App />
					</Auth0Provider>
					<FlagOverridesMenu />
				</LDProvider>
			</ThemeProvider>
		</QueryClientProvider>
	</StrictMode>,
)
